/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useMemo } from 'react'
import moment from 'moment'

import { FormattedMessage } from '@modules/i18n'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { useGetOrganizationQuery } from '@modules/profile-lib/hooks'
import {
  DEFAULT_TRIAL_MP_DAYS,
  getSaasEntityTrialDaysRemaining,
  getSaasLatestTrial,
} from '@modules/profile-lib/trial'
import { getMarketplaceDisplayName } from '@modules/billing-lib/billingDetails/utils'

import SubscriptionOverviewPanel from '../components/SubscriptionOverviewPanel'

const MarketplaceTrial = ({ organizationId }) => {
  const organizationQuery = useGetOrganizationQuery(organizationId)
  const billingDetails = useBillingDetails()

  const trialDescription = useMemo(() => {
    const channel = billingDetails.data?.channel
    const trialDaysRemaining =
      organizationQuery.data && getSaasEntityTrialDaysRemaining(organizationQuery.data)
    const trialEnd = organizationQuery.data && getSaasLatestTrial(organizationQuery.data)?.end

    if (trialDaysRemaining && trialEnd) {
      return (
        <FormattedMessage
          id='trial-overview.marketplace.description.remaining'
          defaultMessage='You have <strong>{trialDaysRemaining, plural, one {{trialDaysRemaining} day} other {{trialDaysRemaining} days}}</strong> remaining on your trial, which will end on {endDate}.{br} After the trial is over, you will get charged for any usage through {channel}. To end the trial or charges thereafter, please delete your resources.'
          values={{
            trialDaysRemaining,
            strong: (content) => <strong>{content}</strong>,
            channel: getMarketplaceDisplayName(channel),
            endDate: moment(trialEnd).format('YYYY-MM-DD [at] HH:mm [UTC]'),
            br: <br />,
          }}
        />
      )
    }

    return (
      <FormattedMessage
        id='trial-overview.marketplace.description.default'
        defaultMessage='Free <strong>{trialLength}-day</strong> trial. After the trial is over, you will get charged for any usage through {channel}.{br} To end the trial or charges thereafter, please delete your resources.'
        values={{
          trialLength: DEFAULT_TRIAL_MP_DAYS,
          strong: (content) => <strong>{content}</strong>,
          channel: getMarketplaceDisplayName(channel),
          br: <br />,
        }}
      />
    )
  }, [billingDetails.data?.channel, organizationQuery.data])

  return (
    <SubscriptionOverviewPanel
      title={<FormattedMessage id='trial-overview.marketplace.title' defaultMessage='Trial' />}
      content={trialDescription}
    />
  )
}

export default MarketplaceTrial
